"use client"

import { useEffect, useState } from "react"
import { ChevronDown, ChevronUp, HelpCircle, PhoneCall, Settings } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useAgentTaskCreateOptions } from "@/hooks/queries/agent-tasks"
import { ToolWorkingHoursSetting } from "@/components/agent-tasks/tool-working-hours-setting"
// Not the package index: it re-exports the tool, which is `server-only`.
import {
  isScheduleCallbackConfigured,
  type ScheduleCallbackConfig,
} from "@/lib/ai/tools/schedule-callback/config"

type ScheduleCallbackToolSettingsProps = {
  scopeType: "email_agent" | "message_agent"
  config: ScheduleCallbackConfig
  enabled: boolean
  disabled?: boolean
  onConfigChange: (config: ScheduleCallbackConfig) => void
  onEnabledChange: (enabled: boolean, config: ScheduleCallbackConfig) => void
  onOpenInstructions: () => void
}

export function ScheduleCallbackToolSettings({
  scopeType,
  config,
  enabled,
  disabled = false,
  onEnabledChange,
  onOpenInstructions,
}: ScheduleCallbackToolSettingsProps) {
  const [expanded, setExpanded] = useState(false)
  const [draftConfig, setDraftConfig] = useState(config)
  const optionsQuery = useAgentTaskCreateOptions(expanded || enabled, scopeType)
  const configured = isScheduleCallbackConfigured(config)

  useEffect(() => setDraftConfig(config), [config])

  function updateConfig(patch: ScheduleCallbackConfig) {
    const nextConfig = { ...draftConfig, ...patch }
    setDraftConfig(nextConfig)
    onEnabledChange(isScheduleCallbackConfigured(nextConfig), nextConfig)
  }

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <div className="overflow-hidden rounded-lg border border-violet-200 bg-violet-50">
        <div className="flex items-center justify-between gap-2 p-4">
          <CollapsibleTrigger asChild>
            <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-left">
              <PhoneCall className="h-4 w-4 text-violet-600" />
              <span className="text-sm font-medium text-gray-700">Schedule Call</span>
              {expanded
                ? <ChevronUp className="ml-auto mr-2 h-4 w-4 text-gray-500" />
                : <ChevronDown className="ml-auto mr-2 h-4 w-4 text-gray-500" />}
            </button>
          </CollapsibleTrigger>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="h-4 w-4 cursor-help text-gray-400" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              Schedule a selected Voice Agent to call a number supplied by the customer or your AI instructions.
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Switch
                  checked={enabled}
                  onCheckedChange={checked => {
                    const nextConfig = checked ? draftConfig : {}
                    setDraftConfig(nextConfig)
                    onEnabledChange(checked, nextConfig)
                  }}
                  disabled={disabled || (!enabled && !configured)}
                />
              </span>
            </TooltipTrigger>
            {!enabled && !configured ? (
              <TooltipContent>Choose a Voice Agent and caller number first.</TooltipContent>
            ) : null}
          </Tooltip>
        </div>
        {!enabled && !configured ? (
          <p className="px-4 pb-3 text-xs font-medium text-amber-700">
            Choose a Voice Agent and caller number before enabling this tool.
          </p>
        ) : null}

        <CollapsibleContent>
          <div className="space-y-4 px-4 pb-4">
            <p className="text-xs text-muted-foreground">
              The selected Voice Agent places the call. When a customer or the system prompt provides a number,
              the tool uses that exact number as the callback destination.
            </p>

            {optionsQuery.isError ? (
              <p className="text-xs text-destructive">Unable to load Voice Agents and outbound numbers.</p>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Voice Agent</Label>
                  <Select
                    value={draftConfig.voice_agent_id ?? ""}
                    onValueChange={voiceAgentId => updateConfig({
                      voice_agent_id: voiceAgentId,
                    })}
                    disabled={disabled || optionsQuery.isLoading}
                  >
                    <SelectTrigger><SelectValue placeholder="Choose an active agent" /></SelectTrigger>
                    <SelectContent>
                      {(optionsQuery.data?.agents ?? []).map(agent => (
                        <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Caller number</Label>
                  <Select
                    value={draftConfig.caller_phone_number ?? ""}
                    onValueChange={callerPhoneNumber => updateConfig({
                      caller_phone_number: callerPhoneNumber,
                    })}
                    disabled={disabled || optionsQuery.isLoading}
                  >
                    <SelectTrigger><SelectValue placeholder="Choose an outbound number" /></SelectTrigger>
                    <SelectContent>
                      {(optionsQuery.data?.phone_numbers ?? []).map(number => (
                        <SelectItem key={number.phone_number} value={number.phone_number}>
                          {number.label ? `${number.label} (${number.phone_number})` : number.phone_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <ToolWorkingHoursSetting id={`${scopeType}-schedule-call-working-hours`}
              checked={draftConfig.respect_working_hours === true}
              onCheckedChange={respectWorkingHours => updateConfig({ respect_working_hours: respectWorkingHours })}
              disabled={disabled} />

            <Button type="button" variant="outline" disabled={disabled}
              onClick={() => { setDraftConfig({}); onEnabledChange(false, {}) }}>
              <span>Clear settings</span>
            </Button>

            <Button type="button" variant="outline" size="sm" onClick={onOpenInstructions}>
              <Settings className="mr-1.5 h-3 w-3" />
              <span>Configure AI Instructions</span>
            </Button>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}
